import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Formik, FieldProps, Form, Field } from 'formik';
import { SpinButton } from '@lifeomic/chroma-react/components/SpinButton';
import {
  ListPatientDiagnosticReportsDocument,
  useListPatientDiagnosticReportsQuery,
} from '../../../../../graphql/queries/__generated__/ListPatientDiagnosticReports.graphql';
import { GetObservationDocument } from '../../../../../graphql/queries/__generated__/GetObservation.graphql';
import {
  usePrevious,
  useServiceAuth,
  useToggle,
  useActiveProjectId,
} from '../../../../../hooks';
import { GetTaskDocument } from '../../../../../graphql/queries/__generated__/GetTask.graphql';
import { useCreateTaskMutation } from '../../../../../graphql/mutations/__generated__/CreateTask.graphql';
import { useDeleteTaskMutation } from '../../../../../graphql/mutations/__generated__/DeleteTask.graphql';
import { useDeleteObservationMutation } from '../../../../../graphql/mutations/__generated__/DeleteObservation.graphql';
import { useUpdateTaskMutation } from '../../../../../graphql/mutations/__generated__/UpdateTask.graphql';
import { useUpdateDiagnosticReportMutation } from '../../../../../graphql/mutations/__generated__/UpdateDiagnosticReport.graphql';
import {
  DiagnosticReportResourceType,
  ExtensionIn,
  ObservationIn,
  ReferenceIn,
  TaskResourceType,
} from '../../../../../util/graphql/api.graphql';
import moment from 'moment';
import {
  TableConfiguration,
  TableModule,
} from '@lifeomic/chroma-react/components/TableModule';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { FormatMessage } from '../../../../../util/intl';
import { useIntl } from 'react-intl';
import SurveyItem from '../../../util/SurveyItem';
import {
  WithIsReadOnlyInjectedProps,
  WithSubjectIdInjectedProps,
} from '../../Contexts';
import { WithValidationInjectedProps } from '../../hoc/withValidation';
import {
  Alert,
  AlertBody,
  AlertIcon,
} from '@lifeomic/chroma-react/components/Alert';
import {
  AlertTriangle,
  Trash,
  Edit,
  FileText,
  Info,
  Plus,
  XCircle,
} from '@lifeomic/chromicons';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { FormikTextArea, FormikTextField } from '../../../../ChromaFormik';
import {
  createTaskReference,
  getGene,
  getVariantType,
  isRecommendation,
  isTargetedVariant,
  isManualFinding,
  createManualFindingObservation,
  getRecommendation,
  setEvidenceLevelValue,
  getEvidenceLevelValue,
  getNotes,
  setNotes,
  omitDeep,
  getTrialId,
  getTaskOutput,
  setTrialId,
} from './observationUtils';
import { ObservationWithTask, VariantType } from './types';
import { isMessageLabel, messages } from './messages';
import { VariantDetail } from './variantDetail/VariantDetail';
import { getTestProps } from '../../../../../util/testProps';
import { ManualFinding } from './ManualFinding';
import { useReportInfoToBugsnag } from '../../../../../util/bugsnagReporting';
import { useCreateDiagnosticReportMutation } from '../../../../../graphql/mutations/__generated__/CreateDiagnosticReport.graphql';
import useStyles from './styles';
import { EvidenceLevelFieldComponent } from './EvidenceLevelFieldComponent';
import { useUpdateObservationMutation } from '../../../../../graphql/mutations/__generated__/UpdateObservation.graphql';
import { getVariantSurveyResponse } from './surveyResponseUtils';
import { EVIDENCE_LEVEL_COMPONENT_CODING } from './constants';
import { Link } from '@lifeomic/chroma-react/components/Link';

export const genomicsReportColumn = (
  formatMessage: FormatMessage,
  subjectId: string,
  handleObservationCreation: (observation: ObservationWithTask) => void,
  handleObservationUpdate: (observation: ObservationWithTask) => void,
  handleEvidenceLevelSelection: (
    observation: ObservationWithTask,
    evidenceLevelValue: fhir.Coding,
  ) => void,
  evidenceLevelValues: fhir.Coding[],
) => [
  {
    header: {
      align: 'left' as const,
      label: formatMessage(messages.details),
    },
    cell: {
      align: 'left' as const,
      content: (observation: ObservationWithTask) => {
        if (isTargetedVariant(observation.code)) {
          const type = getVariantType(observation) as VariantType;
          const gene = getGene(observation);
          return (
            <Box direction="column" gap={3}>
              <Box flexWrap gap={1}>
                <VariantDetail
                  type={type}
                  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                  gene={gene}
                  observation={observation}
                />
              </Box>
            </Box>
          );
        }
        return (
          <Box direction="column" gap={3}>
            <ManualFinding
              observation={observation}
              subjectId={subjectId}
              handleObservationCreation={handleObservationCreation}
              handleObservationUpdate={handleObservationUpdate}
            />
          </Box>
        );
      },
    },
  },
  {
    header: {
      align: 'left' as const,
      label: formatMessage(messages.recommendations),
    },
    cell: {
      align: 'left' as const,
      content: (observation: ObservationWithTask) => (
        <Text size={'body'}>{getRecommendation(observation)}</Text>
      ),
    },
  },
  {
    header: {
      align: 'left' as const,
      label: formatMessage(messages.notes),
    },
    cell: {
      align: 'left' as const,
      content: (observation: ObservationWithTask) => (
        <div>
          {!!evidenceLevelValues?.length && (
            <EvidenceLevelFieldComponent
              evidenceLevelValue={
                getEvidenceLevelValue(
                  observation,
                  EVIDENCE_LEVEL_COMPONENT_CODING,
                ) || null
              }
              evidenceLevelValues={evidenceLevelValues}
              observation={observation}
              handleEvidenceLevelSelection={handleEvidenceLevelSelection}
            />
          )}
          <Text>{getNotes(observation)}</Text>
        </div>
      ),
    },
  },
  {
    header: {
      align: 'left' as const,
      label: formatMessage(messages.trialId),
    },
    cell: {
      align: 'left' as const,
      content: (observation: ObservationWithTask) => (
        <Link
          newTab
          to={`https://clinicaltrials.gov/ct2/show/${getTrialId(observation)}`}
        >
          <Text>{getTrialId(observation)}</Text>
        </Link>
      ),
    },
  },
];

const getReference = ({ reference }: fhir.Reference) => {
  if (reference?.includes('/')) {
    return reference.split('/')[1];
  }
  return reference;
};

interface FormValues {
  extension: {
    id: string;
  }[];
}

const getOrderedDiagnosticReportResult = (
  formValues: FormValues,
  { result }: fhir.DiagnosticReport,
): fhir.Reference[] => {
  if (
    !result ||
    result.length < 2 ||
    !_.some(formValues.extension ?? [], 'id')
  ) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return result;
  }

  const resultIdToReference = result.reduce((acc, current) => {
    const currentId = getReference(current);
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    acc[currentId] = current;

    return acc;
  }, {} as Record<string, fhir.Reference>);

  const { array: existingReferences, map: existingMap } =
    formValues.extension.reduce(
      (acc, current) => {
        const currentReference = resultIdToReference[current.id];

        if (currentReference && currentReference.reference) {
          acc.array.push(currentReference);
          acc.map[currentReference.reference.toLowerCase()] = true;
        }

        return acc;
      },
      {
        array: [] as fhir.Reference[],
        map: {} as Record<string, boolean>,
      },
    );

  const addedReferences = result.reduce((acc, current) => {
    if (current.reference && !existingMap[current.reference.toLowerCase()]) {
      acc.push(current);
    }

    return acc;
  }, [] as fhir.Reference[]);

  return [...existingReferences, ...addedReferences];
};

type GenomicsReportStatus =
  | 'INITIAL'
  | 'MISSING_PATIENT_CONTEXT'
  | 'LOADED'
  | 'LOADING'
  | 'MULTIPLE_DRAFT'
  | 'REFERENCED_RESOURCE_NOT_FOUND';

export interface FieldComponentOwnProps {
  surveyItem: SurveyItem;
}

export type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithValidationInjectedProps &
  WithSubjectIdInjectedProps &
  WithIsReadOnlyInjectedProps;

interface RecommendationNotesForm {
  observationWithTask: ObservationWithTask;
  recommendation: string;
  notes: string;
  trialId?: string;
}

const FieldComponent = (props: FieldComponentProps) => {
  const { formatMessage } = useIntl();

  const projectId = useActiveProjectId();
  const serviceAuth = useServiceAuth();
  const classes = useStyles(props);

  const { error, shouldShowError, subjectId, isReadOnly, surveyItem } = props;

  const [genomicsReportStatus, setGenomicsReportStatus] =
    useState<GenomicsReportStatus>('INITIAL');
  const previousGenomicsReportStatus = usePrevious(genomicsReportStatus);
  const [currentDiagnosticReport, setCurrentDiagnosticReport] =
    useState<fhir.DiagnosticReport>();
  const [observations, setObservations] = useState<ObservationWithTask[]>([]);
  const [currentVariant, setCurrentVariant] = useState<ObservationWithTask>();
  const [isEditVariant, editOn, editOff] = useToggle(false);

  const reportInfoToBugsnag = useReportInfoToBugsnag();

  const context = useMemo(
    () => ({
      headers: {
        'LifeOmic-Account': serviceAuth.account,
        Authorization: `Bearer ${serviceAuth.authToken}`,
      },
    }),
    [serviceAuth?.account, serviceAuth?.authToken],
  );

  const currentVariantType = useMemo(() => {
    if (!currentVariant) {
      return null;
    }
    return getVariantType(currentVariant) as VariantType;
  }, [currentVariant]);

  const currentVariantGene = useMemo(() => {
    if (!currentVariant) {
      return null;
    }
    return getGene(currentVariant);
  }, [currentVariant]);

  const [createDiagnosticReportMutation] = useCreateDiagnosticReportMutation({
    context,
  });

  const newGenomicReport = useCallback(async () => {
    return await createDiagnosticReportMutation({
      variables: {
        effectiveDateTime: new Date().toISOString(),
        subjectRef: `Patient/${subjectId}`,
        datasetId: projectId,
      },
    });
  }, [subjectId, projectId, createDiagnosticReportMutation]);

  const { client: apolloClient } = useListPatientDiagnosticReportsQuery({
    fetchPolicy: 'network-only',
    skip: !subjectId,
    context,
    variables: {
      dateQuery: `le${moment().format('YYYY-MM-DD')}`,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      subjectId: subjectId,
    },
    onCompleted: data => {
      const genomicsReport = (
        data?.patient?.diagnosticReports?.edges || []
      ).map(({ node }) => node);

      const formValues = props.form.values[props.field.name];

      if (
        formValues?.[0]?.system !==
        'http://lifeomic.com/fhir/survey-response-genomics-report-id'
      ) {
        // This is a new survey. Get the genomics report id from the list with registered status:
        const draftReports = genomicsReport.filter(
          diagnosticReport => diagnosticReport.status === 'registered',
        );

        if (draftReports.length === 0) {
          // Create a genomics report:
          newGenomicReport().then(newReportResponse => {
            setCurrentDiagnosticReport(
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              newReportResponse.data
                .createDiagnosticReport as fhir.DiagnosticReport,
            );
            setGenomicsReportStatus('LOADING');
          });
        } else if (draftReports.length > 1) {
          setGenomicsReportStatus('MULTIPLE_DRAFT');
        } else {
          // TODO remove explicit casting when fixing the GQL schema
          setCurrentDiagnosticReport(draftReports[0] as fhir.DiagnosticReport);
          // Trigger targeted variants and recommendations query
          setGenomicsReportStatus('LOADING');
        }
      } else {
        // DiagnosticReport set in the survey form takes precedence. That happens when the user has started the survey,
        // saved it as draft and is now resuming it.
        const initialFormValues = formValues[0];

        const diagnosticReportId = initialFormValues.code.includes('/')
          ? initialFormValues.code.split('/')[1]
          : initialFormValues.code;
        const diagnosticReport = genomicsReport.find(
          ({ id }) => id === diagnosticReportId,
        ) as fhir.DiagnosticReport;

        if (!diagnosticReport) {
          setGenomicsReportStatus('REFERENCED_RESOURCE_NOT_FOUND');
          const msg = `Missing genomics report id ${diagnosticReportId} referenced by survey item ${props.field.name}!`;
          console.error(msg);
          reportInfoToBugsnag(msg);
        } else {
          diagnosticReport.result = getOrderedDiagnosticReportResult(
            initialFormValues,
            diagnosticReport,
          );

          setCurrentDiagnosticReport(diagnosticReport);
          // Trigger targeted variants and recommendations query
          setGenomicsReportStatus('LOADING');
        }
      }
    },
  });

  const [createTask] = useCreateTaskMutation({ context });
  const [updateTask] = useUpdateTaskMutation({ context });
  const [deleteTask] = useDeleteTaskMutation({ context });
  const [deleteObservation] = useDeleteObservationMutation({ context });
  const [updateDiagnosticReport] = useUpdateDiagnosticReportMutation({
    context,
  });
  const [updateObservation] = useUpdateObservationMutation({ context });

  const updateSurveyResponse = useCallback(
    (
      updatedDiagnosticReport: fhir.DiagnosticReport,
      updatedObservations: ObservationWithTask[],
    ) => {
      if (isReadOnly || !updatedDiagnosticReport?.id) {
        return;
      }

      const targetedVariants = updatedObservations
        .map(observation =>
          getVariantSurveyResponse(observation, (label: string) => {
            if (!label) {
              return formatMessage(messages.unknown);
            }
            if (isMessageLabel(label)) {
              return formatMessage(messages[label]);
            }
            return label;
          }),
        )
        .filter(Boolean);

      props.form.setFieldValue(props.field.name, [
        {
          system: 'http://lifeomic.com/fhir/survey-response-genomics-report-id',
          code: `DiagnosticReport/${updatedDiagnosticReport.id}`,
          ...(targetedVariants.length && {
            extension: targetedVariants,
          }),
        } as fhir.CodeableConcept,
      ]);
    },
    [isReadOnly, props.form, props.field.name, formatMessage],
  );

  const addRecommendation = useCallback(
    (
      item: fhir.Observation,
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      event.stopPropagation();
      event.currentTarget.blur();
      editOn();
      setCurrentVariant(item);
    },
    [editOn, setCurrentVariant],
  );

  const updateDiagnosticReportData = useCallback(
    async (updated: fhir.DiagnosticReport) => {
      const diagnosticReport = {
        id: updated.id,
        code: { coding: [{ system: 'http://loinc.org', code: '81247-9' }] },
        resourceType: DiagnosticReportResourceType.DiagnosticReport,
        // TODO improve the casting of nested object
        extension: (updated.extension || []).map(extension =>
          _.omit(extension, '__typename', 'valueReference.__typename'),
        ) as ExtensionIn[],
        result: (updated.result || []).map(
          result =>
            ({
              reference: result.reference,
            } as ReferenceIn),
        ),
      };
      await updateDiagnosticReport({
        variables: {
          diagnosticReport,
        },
        refetchQueries: [
          {
            query: ListPatientDiagnosticReportsDocument,
            variables: {
              dateQuery: `le${moment().format('YYYY-MM-DD')}`,
              subjectId,
            },
          },
          'ListPatientDiagnosticReports',
        ],
      });
    },
    [updateDiagnosticReport, subjectId],
  );

  const updateExtensionTasks = useCallback(
    createdTask => {
      const taskRef = createTaskReference(createdTask.id);
      const extension: fhir.Extension[] =
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        currentDiagnosticReport.extension || [];

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setCurrentDiagnosticReport(prev => {
        const updated = {
          ...prev,
          // save task as a new extension
          extension: [...extension, taskRef],
        };
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        updateDiagnosticReportData(updated).then();
        return updated;
      });
    },
    [
      currentDiagnosticReport,
      setCurrentDiagnosticReport,
      updateDiagnosticReportData,
    ],
  );

  /**
   * update task info in the list of observation data.
   * Notice that this is a temp solution before creating an idempotent way to
   * update the variant list.
   */
  const updateObservationsData = useCallback(
    (observations: ObservationWithTask[], task: fhir.Task) => {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const observationRef = task.input.find((input: any) =>
        isTargetedVariant(input.type),
      )?.valueReference.reference;
      // update observations in-place
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const observationId = observationRef.split('/')[1];
      const index = _.findIndex(observations, { id: observationId });
      if (index > -1) {
        // TODO need to fix GQL schema for type Task in patient-service
        observations[index].task = {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          status: 'draft',
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          intent: 'proposal',
          ...task,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          note: [{ text: task.note?.[0]?.text }],
          output: task.output,
        };
      }
    },
    [],
  );

  /**
   * Initial effect. This effect checks if we have a subject.
   */
  useEffect(() => {
    if (!subjectId) {
      setGenomicsReportStatus('MISSING_PATIENT_CONTEXT');
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This useEffect is responsible in pulling targeted variants and recommendations.
   */
  useEffect(() => {
    if (genomicsReportStatus !== 'LOADING') {
      return;
    }

    const fetchTargetedVariants = async (
      observationIds: string[],
    ): Promise<ObservationWithTask[]> => {
      const result: any[] = [];
      for (const observationId of observationIds) {
        const { data } = await apolloClient.query({
          query: GetObservationDocument,
          variables: { id: observationId },
          context,
        });
        if (!data?.observation) {
          throw new Error(`Could not retrieve observation ${observationId}`);
        }
        // add task to avoid Type error under strict mode
        result.push({ ...data.observation, task: undefined });
      }
      return result.filter(
        result => isTargetedVariant(result.code) || isManualFinding(result),
      );
    };

    const fetchTasks = async (taskIds: string[]): Promise<fhir.Task[]> => {
      const result: fhir.Task[] = [];
      for (const taskId of taskIds) {
        const { data } = await apolloClient.query({
          query: GetTaskDocument,
          variables: { id: taskId },
          context,
        });
        if (!data?.task) {
          throw new Error(`Could not retrieve task ${taskId}`);
        }
        result.push(data.task);
      }
      return result;
    };

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const observationIds = currentDiagnosticReport.result?.map(getReference);

    // step 1: get targeted variants data
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    fetchTargetedVariants(observationIds || [])
      .then(observations => {
        // get task ids
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        const taskIds = currentDiagnosticReport.extension
          ?.filter(isRecommendation)
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          .map(extension => extension.valueReference.reference.split('/')[1]);

        return fetchTasks(taskIds || [])
          .then((tasks: fhir.Task[]) => {
            tasks.forEach((task: fhir.Task) => {
              // TODO remove the ugly unsafe explicit casting when GQL schema is fixed
              updateObservationsData(observations, task as fhir.Task);
            });
          })
          .then(() => {
            setObservations(observations);
            setGenomicsReportStatus('LOADED');
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            updateSurveyResponse(currentDiagnosticReport, observations);
          })
          .catch(error => {
            setObservations([]);
            setGenomicsReportStatus('REFERENCED_RESOURCE_NOT_FOUND');
            const msg = `Unable to retrieve recommendations referenced in genomics report ${
              currentDiagnosticReport?.id
            }, observation ids ${observationIds?.join(
              ',',
            )}, task ids ${taskIds?.join(',')}; ${error.toString()}`;
            console.error(msg);
            reportInfoToBugsnag(msg);
          });
      })
      .catch(error => {
        console.error(
          `Unable to retrieve targeted variants referenced in genomics report ${
            currentDiagnosticReport?.id
          }; ${error.toString()}`,
        );
        setObservations([]);
        setGenomicsReportStatus('REFERENCED_RESOURCE_NOT_FOUND');
        const msg = `Unable to retrieve observations referenced in genomics report ${
          currentDiagnosticReport?.id
        }, observation ids ${observationIds?.join(',')}; ${error.toString()}`;
        console.error(msg);
        reportInfoToBugsnag(msg);
      });
  }, [
    apolloClient,
    context,
    genomicsReportStatus,
    previousGenomicsReportStatus,
    updateObservationsData,
    updateSurveyResponse,
    currentDiagnosticReport,
    reportInfoToBugsnag,
  ]);

  const handleObservationUpdate = useCallback(
    (newObservation: ObservationWithTask) => {
      setObservations(prevObservations => {
        // Update our observations state:
        const newObservations = prevObservations.map(prevObservation => {
          if (prevObservation.id === newObservation.id) {
            return {
              ...newObservation,
              task: prevObservation.task,
            };
          }
          return prevObservation;
        });
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        updateSurveyResponse(currentDiagnosticReport, newObservations);
        return newObservations;
      });
    },
    [currentDiagnosticReport, setObservations, updateSurveyResponse],
  );

  // Update the observation resource externally via graphql and then update our state:
  const handleObservationMutation = useCallback(
    (updatedObservation: ObservationWithTask) => {
      omitDeep(updatedObservation, '__typename');
      updateObservation({
        variables: {
          observation: _.omit(updatedObservation, 'task') as ObservationIn,
        },
      }).then(() => handleObservationUpdate(updatedObservation));
    },
    [updateObservation, handleObservationUpdate],
  );

  const submitVariantRecommendationForm = async (
    form: RecommendationNotesForm,
  ) => {
    const {
      observationWithTask,
      recommendation = '',
      notes = '',
      trialId,
    } = form;
    const { task } = observationWithTask;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    const observationId = currentVariant.id;

    // Update recommendation:
    if (task?.id) {
      const { data } = await updateTask({
        variables: {
          task: {
            id: task.id,
            resourceType: TaskResourceType.Task,
            note: [{ text: recommendation }],
            output: trialId ? getTaskOutput(trialId) : undefined,
          },
        },
      });
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const updatedTask = data.updateTask;
      updateObservationsData(observations, updatedTask as fhir.Task);
    } else {
      // step 1: create task
      const { data } = await createTask({
        variables: {
          datasetId: projectId,
          forRef: `Patient/${subjectId}`,
          note: recommendation,
          observationReference: `Observation/${observationId}`,
          output: trialId ? getTaskOutput(trialId) : undefined,
        },
      });
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      const createdTask = data.createTask;
      // TODO remove the ugly unsafe explicit casting when GQL schema is fixed
      updateObservationsData(observations, createdTask as fhir.Task);
      updateExtensionTasks(createdTask);
    }

    // Update notes:
    let updated = setNotes(observationWithTask, notes);
    if (trialId) updated = setTrialId(updated, trialId);
    handleObservationMutation(updated);

    editOff();
  };

  // swap variant finding with its previous one
  const promoteFinding = useCallback(
    (index: number) => {
      if (index > 0) {
        const targetIndex = index - 1;
        observations[targetIndex] = observations.splice(
          index,
          1,
          observations[targetIndex],
        )[0];
        const newObservations = [...observations];
        setObservations(newObservations);
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        updateSurveyResponse(currentDiagnosticReport, newObservations);
      }
    },
    [currentDiagnosticReport, observations, updateSurveyResponse],
  );

  // swap variant finding with its next one
  const demoteFinding = useCallback(
    (index: number) => {
      const targetIndex = index + 1;
      if (targetIndex < observations.length) {
        observations[targetIndex] = observations.splice(
          index,
          1,
          observations[targetIndex],
        )[0];
        const newObservations = [...observations];
        setObservations(newObservations);
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        updateSurveyResponse(currentDiagnosticReport, newObservations);
      }
    },
    [currentDiagnosticReport, observations, updateSurveyResponse],
  );

  const manualOrderColumn: TableConfiguration = useMemo(
    () => ({
      header: {
        content: () => '',
      },
      cell: {
        content: (_: any, index?: number) => (
          <SpinButton
            decrementDisabled={index === observations.length - 1}
            incrementDisabled={index === 0}
            onIncrement={() => promoteFinding(index!)}
            onDecrement={() => demoteFinding(index!)}
          />
        ),
      },
    }),
    [observations, promoteFinding, demoteFinding],
  );

  const addNewFinding = useCallback(() => {
    // create a new observation in memory
    const newFinding = createManualFindingObservation(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      undefined, // indicate that this has not been persisted yet
      subjectId,
      projectId,
      undefined,
      'registered',
    );
    setObservations(prev => {
      return [...prev, newFinding];
    });
  }, [projectId, subjectId, setObservations]);

  const handleEvidenceLevelSelection = useCallback(
    (
      observation: ObservationWithTask,
      evidenceLevelValue: fhir.Coding | undefined,
    ) => {
      // Create an updated observation resource with the evidence level
      const updatedObservation = setEvidenceLevelValue(
        observation,
        EVIDENCE_LEVEL_COMPONENT_CODING,
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        evidenceLevelValue,
      );

      handleObservationMutation(updatedObservation);
    },
    [handleObservationMutation],
  );

  const handleObservationCreation = useCallback(
    (newObservation: ObservationWithTask) => {
      handleObservationUpdate(newObservation);

      // Update our diagnostic report with this id:
      const updatedDiagnosticReport = {
        ...currentDiagnosticReport,
        result: [
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          ...(currentDiagnosticReport.result || []).map(result =>
            _.omit(result, ['__typename']),
          ),
          {
            reference: `Observation/${newObservation.id}`,
          },
        ],
      };
      return updateDiagnosticReportData(
        updatedDiagnosticReport as fhir.DiagnosticReport,
      );
    },
    [
      currentDiagnosticReport,
      updateDiagnosticReportData,
      handleObservationUpdate,
    ],
  );

  const handleFindingEdit = useCallback(
    (observationId: string) => {
      setObservations(prevObservations => {
        return prevObservations.map(currentObservation => {
          if (currentObservation.id === observationId) {
            return {
              ...currentObservation,
              status: 'registered',
            };
          }
          return currentObservation;
        });
      });
    },
    [setObservations],
  );

  const handleRemoveVariant = useCallback(
    async (
      observationId: string,
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      event.stopPropagation();
      event.currentTarget.blur();

      const { remainingObservations, observationToRemove } =
        observations.reduce(
          (acc, currentObservation) => {
            if (currentObservation.id === observationId) {
              acc.observationToRemove = currentObservation;
            } else {
              acc.remainingObservations.push(currentObservation);
            }

            return acc;
          },
          {
            remainingObservations: [] as ObservationWithTask[],
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            observationToRemove: undefined as ObservationWithTask,
          },
        );

      /**
       * Added this so that there isn't a "pause" between a user clicking remove and the record actually removing
       * while waiting for updateDiagnosticReportData to make the request and refetch observations
       */
      setObservations(remainingObservations);

      await deleteObservation({ variables: { id: observationId } });

      const taskId = observationToRemove.task?.id;

      if (taskId) {
        await deleteTask({
          variables: { id: taskId },
        });
      }

      const remainingDiagnosticResults =
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        (currentDiagnosticReport.result ?? [])
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          .filter(result => !result.reference.includes(observationId));

      const remainingExtension =
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        (currentDiagnosticReport.extension ?? []).filter(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          extension => !extension.valueReference?.reference?.includes(taskId),
        );

      const updatedDiagnosticReport = {
        ...currentDiagnosticReport,
        ...{
          result: remainingDiagnosticResults,
          extension: remainingExtension,
        },
      };

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      await updateDiagnosticReportData(updatedDiagnosticReport);
    },
    [
      currentDiagnosticReport,
      deleteObservation,
      deleteTask,
      observations,
      updateDiagnosticReportData,
    ],
  );

  return (
    <>
      {genomicsReportStatus === 'REFERENCED_RESOURCE_NOT_FOUND' && (
        <Alert statusType="error">
          <AlertIcon icon={XCircle} />
          <AlertBody>
            <Text size="subbody">
              {formatMessage(messages.referencedResourceNotFound)}
            </Text>
          </AlertBody>
        </Alert>
      )}
      {genomicsReportStatus === 'MISSING_PATIENT_CONTEXT' && (
        <Alert statusType="warning">
          <AlertIcon icon={AlertTriangle} />
          <AlertBody>
            <Text size="subbody">
              {formatMessage(messages.missingPatientContext)}
            </Text>
          </AlertBody>
        </Alert>
      )}
      {genomicsReportStatus === 'MULTIPLE_DRAFT' && (
        <Alert statusType="error">
          <AlertIcon icon={XCircle} />
          <AlertBody>
            <Text size="subbody">
              {formatMessage(messages.multipleDraftReports)}
            </Text>
          </AlertBody>
        </Alert>
      )}
      {['INITIAL', 'LOADING', 'LOADED'].includes(genomicsReportStatus) && (
        <div>
          {observations.filter(observation =>
            isTargetedVariant(observation.code),
          ).length === 0 && (
              <div className={classes.addNewFinding}>
                <Button icon={Plus} color="inverse" onClick={addNewFinding}>
                  {formatMessage(messages.addNewFinding)}
                </Button>
              </div>
            ) && (
              <Alert>
                <AlertIcon icon={Info} />
                <AlertBody>
                  <Text size="subbody">
                    {formatMessage(messages.emptySelection, {
                      link: (
                        <a
                          href={`/phc/project/subjects/${subjectId}/omics-explorer`}
                          className={classes.obviousLink}
                        >
                          {formatMessage(messages.omicsExplorer)}
                        </a>
                      ),
                    })}
                  </Text>
                </AlertBody>
              </Alert>
            )}
          <TableModule
            config={[
              manualOrderColumn,
              ...genomicsReportColumn(
                formatMessage,
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                subjectId,
                handleObservationCreation,
                handleObservationUpdate,
                handleEvidenceLevelSelection,
                surveyItem?.evidenceLevelValues || [],
              ),
            ]}
            data={observations}
            isLoading={genomicsReportStatus !== 'LOADED'}
            maxCellWidth={2}
            wrapCellContent
            rowActions={(item: ObservationWithTask) => (
              <>
                {isManualFinding(item, 'preliminary') && (
                  <Tooltip title={formatMessage(messages.editFinding)}>
                    <IconButton
                      aria-label={formatMessage(messages.editFinding)}
                      color="inverse"
                      icon={Edit}
                      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                      onClick={() => handleFindingEdit(item.id)}
                      {...getTestProps(`edit-finding-${item.id}`)}
                    />
                  </Tooltip>
                )}
                <Tooltip title={formatMessage(messages.addRecommendation)}>
                  <IconButton
                    aria-label={formatMessage(messages.addRecommendation)}
                    color="inverse"
                    icon={FileText}
                    disabled={isReadOnly}
                    onClick={e => {
                      addRecommendation(item, e);
                    }}
                    {...getTestProps(
                      `add-genomics-report-recommendation-${item.id}`,
                    )}
                  />
                </Tooltip>
                <Tooltip title={formatMessage(messages.removeVariant)}>
                  <IconButton
                    aria-label={formatMessage(messages.removeVariant)}
                    color="inverse"
                    icon={Trash}
                    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                    onClick={e => handleRemoveVariant(item.id, e)}
                    {...getTestProps(
                      `remove-genomics-report-variant-${item.id}`,
                    )}
                  />
                </Tooltip>
              </>
            )}
          />
          {observations.filter(observation =>
            isManualFinding(observation, 'registered'),
          ).length === 0 && (
            <div className={classes.addNewFinding}>
              <Button icon={Plus} color="inverse" onClick={addNewFinding}>
                {formatMessage(messages.addNewFinding)}
              </Button>
            </div>
          )}
        </div>
      )}
      {shouldShowError && (
        <Text size="caption" className={classes.errorText}>
          {error}
        </Text>
      )}
      {isEditVariant && (
        <Modal
          actions={
            <>
              <Button onClick={editOff} variant="text">
                {formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="contained"
                form="variant-recommendation-notes"
                type="submit"
              >
                {formatMessage(messages.save)}
              </Button>
            </>
          }
          isOpen={isEditVariant}
          justifyActions="flex-end"
          onDismiss={editOff}
          title={formatMessage(messages.addRecommendationAndNotes)}
          fullWidth
        >
          <Box direction="column" gap={3}>
            <Box flexWrap gap={1}>
              <VariantDetail
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                type={currentVariantType}
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                gene={currentVariantGene}
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                observation={currentVariant}
              />
            </Box>
            <Formik
              initialValues={
                {
                  observationWithTask: currentVariant,
                  recommendation: getRecommendation(currentVariant),
                  notes: getNotes(currentVariant),
                  trialId: getTrialId(currentVariant),
                } as RecommendationNotesForm
              }
              onSubmit={submitVariantRecommendationForm}
              render={({ handleSubmit }) => (
                <Form
                  id={'variant-recommendation-notes'}
                  onSubmit={handleSubmit}
                >
                  <Box className={classes.recommendationAndNotes}>
                    <Field
                      fullWidth
                      name="recommendation"
                      label={formatMessage(messages.recommendations)}
                      aria-label={formatMessage(messages.recommendations)}
                      component={FormikTextArea}
                    />
                    <Field
                      fullWidth
                      name="notes"
                      label={formatMessage(messages.notes)}
                      aria-label={formatMessage(messages.notes)}
                      component={FormikTextArea}
                    />
                    <Field
                      fullWidth
                      name="trialId"
                      label={formatMessage(messages.trialId)}
                      aria-label={formatMessage(messages.trialId)}
                      component={FormikTextField}
                    />
                  </Box>
                </Form>
              )}
            />
          </Box>
        </Modal>
      )}
    </>
  );
};

export default FieldComponent;
